<template>
    <div v-if="sessionActive">
        <v-container fluid>

            <v-card flat>

                <material-header
                    title="Data variables"
                    info="Create, manage and analyze"
                    help="data-variables"
                    icon="mdi-database">

                    <template slot="buttons">

                        <v-tooltip color="primary" left v-if="hasPermission('view data variables')">
                            <template v-slot:activator="{ on }">
                                <v-btn @click="refresh()" v-on="on" icon>
                                    <v-icon>mdi-refresh</v-icon>
                                </v-btn>
                            </template>
                            <span>Reload data</span>
                        </v-tooltip>

                        <v-tooltip color="primary" left v-if="hasPermission('create data variables')">
                            <template v-slot:activator="{ on }">
                                <v-btn @click="addGroup()" v-on="on" icon>
                                    <v-icon>mdi-gamepad-circle</v-icon>
                                </v-btn>
                            </template>
                            <span>Create new data variable group</span>
                        </v-tooltip>

                        <v-tooltip left color="primary">
                            <template v-slot:activator="{ on }">
                                <v-btn :disabled="!hasPermission('create data variables')"
                                       v-on="on"
                                       fab
                                       class="ml-2"
                                       small
                                       color="primary"
                                       @click="createButtonClicked()">
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                            </template>
                            <span>Create new data variable</span>
                        </v-tooltip>

                    </template>
                </material-header>

                <v-alert v-if="!hasPermission('view data variables')"
                         class="body-2"
                         type="error"
                >
                    Your user account doesn't have permission to view data variables
                </v-alert>

                <v-card flat class="pl-2 pr-2 pt-4 pb-4 d-flex flex-wrap justify-space-around"
                        v-if="hasPermission('view data variables')">

                    <div class="align-self-center flex-grow-1 ml-3">

                        <!-- STANDARD QUESTIONS BUTTON -->
                        <v-tooltip top color="primary">
                            <template v-slot:activator="{ on }">
                                <v-btn color="primary"
                                       small
                                       left
                                       v-on="on"
                                       @click="addStandardVariablesClicked">
                                    <v-icon small class="mr-2">mdi-plus</v-icon>
                                    STANDARD
                                </v-btn>
                            </template>
                            Add standard data variables
                        </v-tooltip>

                        <!-- ASSIGN TO GROUP BUTTON -->
                        <v-btn color="primary"
                               v-show="assignButtonVisible"
                               small
                               class="ml-1"
                               :disabled="selected.length === 0 || !hasPermission('update data variables')"
                               @click="showGroupSelection()">
                            Assign to group
                        </v-btn>

                    </div>

                    <!-- SEARCH FIELD -->
                    <v-card flat width="300">
                        <v-text-field
                            class="ml-4 align-self-center"
                            hide-details
                            :height="32"
                            prepend-icon="mdi-magnify"
                            label="Search"
                            v-model="searchTerm"
                            clearable
                            solo
                            dense
                            background-color="grey darken-2"
                        ></v-text-field>
                    </v-card>

                    <!-- FILTER BY GROUP -->
                    <v-card flat width="300">
                        <v-autocomplete
                            class="ml-4 align-self-center"
                            clearable
                            hide-details
                            prepend-icon="mdi-gamepad-circle"
                            :items="groupNames"
                            v-model="filterByGroup"
                            label="Filter by group"
                            solo
                            dense
                            background-color="grey darken-2"
                        ></v-autocomplete>
                    </v-card>

                    <!-- CLASS FILTER -->
                    <v-card flat width="300">
                        <v-select
                            class="ml-4 align-self-center"
                            prepend-icon="mdi-filter"
                            v-model="typeFilter"
                            :items="typesAvailable"
                            item-text="name"
                            item-value="value"
                            hide-details
                            clearable
                            placeholder="Filter by class"
                            solo
                            dense
                            background-color="grey darken-2"
                        ></v-select>
                    </v-card>
                </v-card>
            </v-card>

            <v-data-table
                v-model="selected"
                :headers="headers"
                :items="items"
                item-key="id"
                show-select
                :search="searchTerm"
                :loading="loading"
                :options.sync="tableOptionsConfig"
                :footer-props="{itemsPerPageOptions: [25,50,100, -1]}"
            >
                <!-- ITEMS -->
                <template v-slot:item="{ item, isSelected, select }">
                    <tr>
                        <!-- CHECKBOX -->
                        <td class="text-left" style="width: 1px">

                            <v-checkbox
                                class="mt-1"
                                color="primary"
                                hide-details
                                :value="isSelected"
                                @change="select"
                                @click.capture.stop=""
                            ></v-checkbox>
                        </td>

                        <!-- NAME -->
                        <td v-if="hasPermission('update data variables')"
                            class="text-left cursor-pointer"
                            @click="edit(item)"
                        >
                            <v-tooltip top color="primary">
                                <template v-slot:activator="{ on }">
                                    <span v-on="on">
                                        {{ item.name }}
                                    </span>
                                </template>
                                <div @click="edit(item)">Click to edit data variable</div>
                            </v-tooltip>
                        </td>
                        <td v-else>
                            {{ item.name }}
                        </td>

                        <!-- TYPE -->
                        <td class="text-left">
                            <data-variable-chip :type="item.type"></data-variable-chip>
                        </td>

                        <!-- GROUP -->
                        <td class="text-left">
                            <group-tag :group="item.group"/>
                        </td>

                        <!-- STATS BUTTON -->
                        <td>
                            <v-tooltip left color="primary" v-if="hasPermission('view projects')">
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on"
                                           fab
                                           class="elevation-0"
                                           x-small
                                           color="primary"
                                           @click.stop="openStats(item)">
                                        <v-icon small>mdi-poll</v-icon>
                                    </v-btn>
                                </template>
                                <span>Show statistics</span>
                            </v-tooltip>
                        </td>

                        <!-- UPDATED AT -->
                        <td class="text-left">
                            <div style="white-space: nowrap;">
                                <v-tooltip top color="primary">
                                    <template v-slot:activator="{ on }">
                                        <span v-on="on">
                                            {{ toHumanFrom(item.updated_at) }}
                                        </span>
                                    </template>
                                    <span>
                                        Created at:
                                        {{ toLocalDateTime(item.created_at) }}<br>

                                        Updated at:
                                        {{ toLocalDateTime(item.updated_at) }}
                                    </span>
                                </v-tooltip>
                            </div>
                        </td>

                        <!-- ACTIONS -->
                        <td class="text-left pa-0">

                            <div style="white-space: nowrap;" v-if="!item.system">

                                <!-- DELETE BUTTON -->
                                <v-tooltip top color="primary">
                                    <template v-slot:activator="{ on }">
                                        <v-btn :disabled="!hasPermission('delete data variables')"
                                               class="ma-0"
                                               icon
                                               text
                                               v-on="on"
                                               @click.stop="deleteDataVariableClicked(item)"
                                               color="secondary">
                                            <v-icon light small color="white">mdi-delete</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Delete data variable</span>
                                </v-tooltip>
                            </div>

                        </td>
                    </tr>
                </template>
            </v-data-table>

        </v-container>

        <!-- CREATE DIALOG -->
        <create-dialog
            v-model="createDialogModel"
            :types="dataVariableTypes"
            :key="createKey"
            @change="createDialogCompleted"
        ></create-dialog>

        <!-- EDIT GROUP DIALOG -->
        <groups-dialog v-model="groupEditVisible"
                       @close="groupEditVisible=false"
        />

        <!-- SELECT GROUP DIALOG -->
        <select-group v-model="selectGroupVisible"
                      :recent-groups="recentGroups"
                      :groups="alphaSortedGroups"
                      @select="groupSelected"
                      @close="selectGroupVisible=false"
        />

        <!-- VARIABLE STATS DIALOG -->
        <v-dialog
            :key="dataVariableStats.id"
            v-model="dataVariableStatsDialogModel"
            :width="1100"
            hide-overlay
            transition="fade-transition"
        >
            <data-variable-stats :dataVariable="dataVariableStats" @close="closeStatsClicked">

            </data-variable-stats>

        </v-dialog>

        <!-- DELETE CONFIRMATION DIALOG -->
        <confirmation-dialog v-model="deleteDialogModel"
                             headline="DELETE CONFIRMATION"
                             delete
                             confirm-button-text="Delete"
                             abort-button-text="Cancel"
                             @selected="deleteDataVariableDo">
            Deleting this data variable will cause permanent data loss! Are you sure?

            <stat-box
                class="mt-3"
                :value="deleteCount"
                hint="Total number of records to be deleted"
                color="error"
                :loading="loading"
            ></stat-box>

        </confirmation-dialog>

        <!-- STANDARD VARIABLES DIALOG -->
        <standard-variables-dialog v-if="dataVariables"
                                   v-model="standardVariablesDialogModel"
                                   @input="standardVariablesDialogModel=false"
        ></standard-variables-dialog>
    </div>
</template>

<script>

import Store from '../store';
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import EditDataVariable from './EditDataVariable';
import GroupTag from './GroupTag';
import tableSorterMixin from '../../../components/mixins/tableSorterMixin';
import GroupsDialog from './GroupsDialog';
import SelectGroup from './SelectGroup';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import DataVariableStats from './Stats/DataVariableStats';
import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import CreateDialog from './CreateDialog';
import Json from 'vue-json-pretty';
import DataVariableChip from '../../../components/UI/DataVariableChip';
import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";
import StatBox from '../../../components/UI/StatBoxNumerical';
import StandardVariablesDialog from "./StandardVariablesDialog";

export default {

    mixins: [tableSorterMixin, activeSessionChecker, timeDisplay],

    components: {
        StandardVariablesDialog,
        EditDataVariable,
        GroupTag,
        GroupsDialog,
        SelectGroup,
        MaterialHeader,
        DataVariableStats,
        CreateDialog,
        Json,
        DataVariableChip,
        ConfirmationDialog,
        StatBox
    },
    data() {
        return {
            form: null,

            headers: [
                {text: 'NAME', value: 'name'},
                {text: 'TYPE', value: 'type', width: '1px'},
                {text: 'GROUP', sortable: false, width: '1px'},
                {text: 'STATS', sortable: false, width: '1px'},
                {text: 'UPDATED', value: 'updated_at', width: '100px'},
                {text: '', value: '', sortable: false, width: '40px'},
            ],
            createKey: 1,
            groupEditVisible: false,
            selectGroupVisible: false,
            dataVariableStatsDialogModel: false,
            dataVariableStats: {},

            createDialogModel: false,
            loading: false,
            selected: [],
            deleteCount: 0,
            deleteCountLoading: false,
            deleteDialogModel: false,
            deleteVariable: null,

            typesAvailable: [
                {name: 'System variables', value: 'system'},
                {name: 'Personal information (PI)', value: 'pi'},
                {name: 'Panelist writable', value: 'panelist_writable'},
                {name: 'Registration / import writable', value: 'create_writable'},
                {name: 'Editable', value: 'user_editable'},
                {name: 'Translatable', value: 'user_localizable'},
                {name: 'Community API', value: 'community_api_writable'},
                {name: 'Dynamic Profile', value: 'dynamic_profiling_when_missing'},
                {name: 'Dynamic Profile by age', value: 'dynamic_profiling_by_age'},
            ],

            standardVariablesDialogModel: false
        }
    },

    computed: {

        typeFilter: {
            get() {
                return this.typeFilterModel;
            },
            set(type) {
                this.setTypeFilterModel(type)
            }
        },

        filterByGroup: {
            get() {
                return this.filterByGroupModel;
            },
            set(value) {
                this.setFilterByGroupModel(value);
            }
        },

        searchTerm: {
            get() {
                return this.searchTermModel;
            },
            set(value) {
                this.setSearchTermModel(value);
            }
        },

        tableOptionsConfig: {
            get() {
                return this.tableOptions;
            },
            set(value) {
                this.setTableOptions(value);
            }
        },

        standardVariablesMissing() {

        },

        assignButtonVisible() {
            return this.selected.length > 0;
        },

        groupNames() {
            // Extract name into an array
            return [...['NO GROUP'], ...this.groups.map(a => a.name)];
        },

        items() {
            return this.getFilteredDataVariables(this.filterByGroup);
        },

        // Find data variable locales based on locales question_text options
        dataVariableLocales() {
            let locales = [];
            if (this.dataVariable && this.settings.locales) {
                this.dataVariable.locales.forEach(locale => {
                    const found = this.settings.locales.find(l => locale.locale_id === l.id);
                    if (!found) {
                        throw new Error('Question option locale could not be found!');
                    }
                    locales.push(found);
                });
            }
            return locales;
        },

        // Root store
        ...mapState([
            'dataVariables',
            'dataVariableTypes',
            'dataVariablesLoading'
        ]),

        // Module store
        ...mapState('DataVariables', [
            'filterByGroupModel',
            'searchTermModel',
            'groups',
            'typeFilterModel',
            'tableOptions',
        ]),

        ...mapGetters('DataVariables', [
            'getFilteredDataVariables',
            'recentGroups',
            'alphaSortedGroups'
        ]),
    },

    methods: {

        // Root actions
        ...mapActions([
            'getDataVariables',
            'getDataVariableOptions',
            'deleteDataVariable',
            'getDataVariableDataCount'
        ]),

        // Module actions
        ...mapActions('DataVariables', [
            'getGroups',
            'assignToGroup',
        ]),

        // Module actions
        ...mapMutations('DataVariables', [
            'setFilterByGroupModel',
            'setSearchTermModel',
            'setTypeFilterModel',
            'setTableOptions'
        ]),

        addStandardVariablesClicked() {
            this.standardVariablesDialogModel = true;
        },

        // User initialized refresh
        refresh() {
            Bus.$emit('flash-message', {message: 'Refreshing...'});
            this.loadData(true);
        },

        loadData(force = false) {

            this.loading = true;

            let promise = Promise.all([this.getDataVariables(force), this.getGroups(force)]);

            promise.then(() => {
                this.loading = false;
            }).catch(error => {
                Bus.$emit('server-error');
            });
        },

        createButtonClicked() {
            this.createKey++;

            this.$nextTick(() => {
                this.createDialogModel = true;
            });
        },

        // Data contains type and locales[]
        createDialogCompleted(data) {
            this.createDialogModel = false;
            this.$router.push({name: 'data-variable-create', params: data});
        },

        edit(dataVariable) {
            this.$router.push({
                name: 'data-variable-edit',
                params: {id: dataVariable.id}
            });
        },

        addGroup() {
            this.groupEditVisible = true;
        },

        showGroupSelection() {
            this.selectGroupVisible = true;
        },

        groupSelected(group) {

            let selected = [...this.selected];

            this.selectGroupVisible = false;
            this.selected = [];

            this.assignToGroup({group: group, dataVariables: selected})
                .then(() => {

                })
                .catch(() => {
                    this.selected = [];
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'Sorry we could not assign variable(s) to the selected group!'
                    });
                });
        },

        openStats(dataVariable) {
            this.dataVariableStats = dataVariable;
            this.dataVariableStatsDialogModel = true;
        },

        closeStatsClicked() {
            this.dataVariableStatsDialogModel = false;
        },

        deleteDataVariableClicked(dataVariable) {

            if (dataVariable.system) {
                Bus.$emit('flash-message', {
                    type: 'error',
                    message: 'This is a system variable that cannot be deleted'
                });
            } else {

                this.deleteCountLoading = true;
                this.deleteVariable = dataVariable;
                this.deleteDialogModel = true;

                this.getDataVariableDataCount(dataVariable.id)
                    .then(data => {
                        this.deleteCount = data.count;
                    })
                    .catch(error => {
                        Bus.$emit('server-error');
                    })
                    .finally(() => {
                        this.deleteCountLoading = false;
                    });
            }
        },

        deleteDataVariableDo(confirmed) {

            this.deleteDialogModel = false;

            if (confirmed) {
                this.deleteDataVariable(this.deleteVariable.id)
                    .then(() => {
                        Bus.$emit('flash-message', {message: 'Data variable successfully deleted...'});
                    })
                    .catch(error => {
                        Bus.$emit('flash-message', {type: 'error', message: error.response.data.message});
                    });
            }
        },
    },

    created() {

        if (!this.$store.state.DataVariables) {
            this.$store.registerModule('DataVariables', Store);

            if (this.hasPermission('view data variables')) {
                this.loadData();
            }
        }
    },

    beforeDestroy() {
        if (this.$store.state.environment !== 'local') {
            this.$store.unregisterModule('DataVariables');
        }
    },
}

</script>
